/**
 * HubShell
 *
 * Minimal authenticated-shell layout for workspace-less routes (currently only
 * /dashboard). Provides:
 *   - The sidebar (workspace switcher, nav, user dropdown, theme + collapse)
 *   - The shadcn SidebarProvider for collapse state
 *   - A content area that hosts the page via <Outlet />
 *
 * Deliberately does NOT include:
 *   - Property/workspace data fetching (no context to fetch against)
 *   - Trial / past-due / paywall banners (those are property-scoped)
 *   - AI sidebar (the hub isn't a query target)
 *   - Editor mode provider
 *
 * Use the full DashboardLayout for property/workspace routes where those
 * concerns apply. Use HubShell for /dashboard and other workspace-less
 * authenticated landings.
 */

import { useState, useEffect } from 'react'
import { AgentPlanRedirect } from '@/components/shared/AgentPlanRedirect'
import { Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import { Sidebar } from '@/components/layout/Sidebar'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getAllWorkspaces } from '@/lib/api/workspaces'
import { generateSlugId } from '@/lib/routing/slug-id'

const SIDEBAR_COLLAPSED_KEY = 'humblytics-sidebar-collapsed'

/**
 * Resolve a default workspace slugId so sidebar nav items on /dashboard route to
 * something real. Sidebar's getBasePath() returns '' when there's no workspace or
 * property in the URL — that produces broken hrefs like `/traffic` instead of
 * `/workspace/<slug>/traffic`. By passing a workspaceSlugId here we give the
 * sidebar a target for all its nav links.
 */
function useDefaultWorkspaceSlugId(): string | undefined {
  const { token } = useAuth()
  const { data } = useQuery({
    queryKey: ['hub-shell-default-workspace', token],
    queryFn: async () => {
      if (!token) return []
      return getAllWorkspaces(token)
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  })

  if (!Array.isArray(data) || data.length === 0) return undefined
  const ws = data[0]
  return generateSlugId(ws.name || ws.workspaceId, ws.workspaceId)
}

export default function HubShell() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isSidebarCollapsed))
    } catch {
      // localStorage unavailable — fail silent
    }
  }, [isSidebarCollapsed])

  const workspaceSlugId = useDefaultWorkspaceSlugId()

  return (
    <SidebarProvider defaultOpen={!isSidebarCollapsed}>
      <AgentPlanRedirect />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        workspaceSlugId={workspaceSlugId}
      />
      <SidebarInset className="flex flex-col min-h-0 min-w-0 h-full overflow-hidden">
        {/* The hub page renders its own 52px bar ("Home" + Add site); no ShellHeader here. */}
        <div className="flex-1 min-h-0 overflow-auto">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
